export interface NewsCommentAuthorProfile {
  userId: string;
  userName: string;
  userEmail: string | null;
  wechatAvatar: string | null;
  wechatNickname: string | null;
}

export interface NewsCommentAuthorBadge {
  avatarUrl: string | null;
  fallback: string;
}

function getTextInitial(value: string | null | undefined) {
  const normalized = value?.trim();
  if (!normalized) {
    return "";
  }

  const first = [...normalized].find((char) => /[\p{L}\p{N}]/u.test(char));
  return (first || normalized[0] || "").toLocaleUpperCase();
}

function createSeededRng(seed: string) {
  let hash = 2166136261;

  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  let state = hash >>> 0;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return (state >>> 0) / 4294967296;
  };
}

function buildIdenticonAvatar(seed: string) {
  const random = createSeededRng(seed);
  const hue = Math.floor(random() * 360);
  const bg = `hsl(${hue} 24% 18%)`;
  const fg = `hsl(${(hue + 26) % 360} 72% 62%)`;
  const muted = `hsl(${(hue + 130) % 360} 58% 34%)`;

  const blocks: string[] = [];
  const size = 5;
  const half = Math.ceil(size / 2);
  const tile = 9;

  for (let row = 0; row < size; row++) {
    for (let col = 0; col < half; col++) {
      if (random() <= 0.5) {
        continue;
      }

      const x = col * tile;
      const mirrorX = (size - 1 - col) * tile;
      const y = row * tile;
      const color = random() > 0.5 ? fg : muted;

      blocks.push(
        `<rect x="${x}" y="${y}" width="${tile}" height="${tile}" rx="1" fill="${color}" />`,
      );
      if (mirrorX !== x) {
        blocks.push(
          `<rect x="${mirrorX}" y="${y}" width="${tile}" height="${tile}" rx="1" fill="${color}" />`,
        );
      }
    }
  }

  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 45 45' width='45' height='45' shape-rendering='crispEdges'><rect width='45' height='45' fill='${bg}'/>${blocks.join("")}</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function getPriorityInitial(profile: NewsCommentAuthorProfile) {
  return (
    getTextInitial(profile.userName) ||
    getTextInitial(profile.userEmail) ||
    getTextInitial(profile.wechatNickname)
  );
}

export function getNewsCommentAuthorBadge(
  profile: NewsCommentAuthorProfile,
): NewsCommentAuthorBadge {
  const safeAvatar = profile.wechatAvatar?.trim();

  if (safeAvatar) {
    return {
      avatarUrl: safeAvatar,
      fallback: getPriorityInitial(profile),
    };
  }

  const initial = getPriorityInitial(profile);
  if (initial) {
    return {
      avatarUrl: null,
      fallback: initial,
    };
  }

  return {
    avatarUrl: buildIdenticonAvatar(profile.userId),
    fallback: "ID",
  };
}

export function getNewsCommentAuthorName(profile: NewsCommentAuthorProfile) {
  if (profile.userName) {
    return profile.userName;
  }
  if (profile.wechatNickname) {
    return profile.wechatNickname;
  }
  return "匿名用户";
}
